// ════════════════════════════════════════════════════════════
//  ui/circuitStore.js — the Circuit's numbers, shared by every screen
// ════════════════════════════════════════════════════════════
//  One poller (only while something is showing it): this week's leagues,
//  ledger rows and standings, and how far behind the keeper is. The board,
//  the Ratings board and every Odds panel read the same snapshot, so they
//  can never disagree with each other.
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { fbNet as net } from "../online/net.js";
import { serverNow } from "../online/ie.js";
import { ledgerOf, leagueOf } from "../online/ledger.js";
import { weekOf, standings, GENESIS_LEAGUE, seasonOf, seasonWeeks, weekOfSeason, racingDayOf, hourOf, dayIsV3, leagueOn, salvationOf, salvationLine, standoutLine, crownsOf } from "../online/circuit.js";
import { leagueCtx } from "../online/challengers.js";
import { foldMinds, genesisMinds } from "../online/mind.js";

const REFRESH_MS = 8000;
let state = { ready: false, week: null, league: null, rows: [], S: new Map(), S0: new Map(), season: null, minds: null, behind: 0, keeping: false, at: 0 };
const subs = new Set();
let users = 0, timer = null, running = false;
const emit = () => { for (const f of subs) f(state); };
export const setCircuit = (patch) => { state = { ...state, ...patch }; emit(); };
export const circuitNow = () => state;

async function refresh() {
  if (running) return;
  running = true;
  try {
    const w = weekOf(serverNow());
    const fresh = await leagueOf(net, w);
    // a new week whose leagues aren't written yet (the keeper is still finishing last week): say so
    // CHALLENGERS (2026-10-21): everyone who has joined is on the table — in the Contenders from the card after they joined
    // + the week's cup check-ins, read exactly as the stage reads them — so a bracket on the board is the bracket being fought
    const L = await leagueCtx(net, hourOf(serverNow()) + 1, fresh || (state.league && state.league.base) || state.league || { ...GENESIS_LEAGUE, elo: {} }, serverNow()).catch(() => null);
    const base = L ? L.league : (fresh || (state.league && state.league.base) || state.league || { ...GENESIS_LEAGUE, elo: {} }), members = L ? L.members : new Map(), entered = L ? L.entered : null;
    const rows = await ledgerOf(net).rows(w);
    // V3 (2026-10-22): the League as TODAY has it (the week's escapes in) — and the week's Salvation points + the line
    const day = racingDayOf(serverNow()), v3 = dayIsV3(day);
    const league = v3 ? leagueOn(base, rows, day) : base;
    const points = v3 ? salvationOf(base, rows, base.elo || null) : null;
    // the season: this week plus the weeks before it in the same season (Crowns simply added up)
    const sn = seasonOf(w), past = seasonWeeks(sn).filter((x) => x < w);
    const seasonRows = (await Promise.all(past.map((x) => ledgerOf(net).rows(x)))).flat().concat(rows);
    // where everyone stood when today began — the board's ▲▼
    const today = racingDayOf(serverNow()), before = (r) => racingDayOf(r.at) < today;
    setCircuit({
      ready: true, week: w, league, members, entered, leaguePending: !fresh, rows, at: Date.now(), v3, points, line: points ? salvationLine(points, base) : null, standout: points ? standoutLine(points, league) : null, crowns: crownsOf(rows),
      S: standings(rows, league.elo), S0: standings(rows.filter(before), league.elo),
      minds: foldMinds(league.minds || genesisMinds(), rows),   // every NPC's mind as of now (mind.js)
      season: { n: sn + 1, week: weekOfSeason(w), rows: seasonRows, S: standings(seasonRows), S0: standings(seasonRows.filter(before)) },
    });
  } catch (e) { console.warn("[circuit]", e); } // eslint-disable-line no-console
  running = false;
}
export const refreshCircuit = refresh;

/** The shared snapshot (starts the poller while anything is using it). */
export function useCircuit(enabled = true) {
  const [s, set] = useState(state);
  useEffect(() => {
    if (!enabled) return undefined;
    subs.add(set); users++;
    if (!timer) { refresh(); timer = setInterval(refresh, REFRESH_MS); }
    set(state);
    return () => { subs.delete(set); users--; if (!users && timer) { clearInterval(timer); timer = null; } };
  }, [enabled]);
  return s;
}

// ── favourites (caP'n: "so you can track your favorite npc") — per browser ─
const FAV = "shatterproof.favs";
const readFav = () => { try { return new Set(JSON.parse(localStorage.getItem(FAV) || "[]")); } catch { return new Set(); } };
let favs = readFav();
const favSubs = new Set();
export function toggleFav(key) {
  favs = new Set(favs);
  if (favs.has(key)) favs.delete(key); else favs.add(key);
  try { localStorage.setItem(FAV, JSON.stringify([...favs])); } catch { /* private mode: this tab only */ }
  for (const f of favSubs) f(favs);
}
export function useFavs() {
  const [f, set] = useState(favs);
  useEffect(() => { favSubs.add(set); return () => { favSubs.delete(set); }; }, []);
  return f;
}

// ── the being page that's open (any board can open one) ──────
let openKey = null;
const openSubs = new Set();
export function openBeing(key) { openKey = key; for (const f of openSubs) f(openKey); }
export function useOpenBeing() {
  const [k, set] = useState(openKey);
  useEffect(() => { openSubs.add(set); return () => { openSubs.delete(set); }; }, []);
  return k;
}
